// ── CallRegister 핵심 상태 훅 ────────────────────────────────────────────────
// - useState: 로딩, 저장, 목록, 폼, 수정 모달, 킵 해제 모달, 필터, 컬럼 너비
// - useRef: 휴일 캐시, 리사이즈 드래그, textarea 자동높이
// - useMemo: 컬럼 템플릿, 최소 너비, 파생 필터 목록, 헤더 레이블 등

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_FORM, FORM_COLUMNS, LIST_COLUMNS, REQUIRED_FIELDS,
  MIN_COL_WEIGHT, MIN_COL_PX,
} from './constants';
import {
  makeFluidTemplate, compareByCallDateDescThenNameAsc,
} from './utils';
import { toDateString, todayKST, nowKST } from '../../../utils/date';

// 목록 한 페이지 줄 수 (2026-10-23 사용자 선택 — "계약관리처럼 1,2,3 페이지", 20줄)
const CALL_LIST_PAGE_SIZE = 20;

// companyCode: 외부 회사 일일콜현황일 때 회사 코드 ('LNJ' 등).
//   메뉴·라우트가 회사별로 갈리므로(/my/tm-calls-lnj → companyCode="LNJ") 이 값이 곧 "어느 회사 콜인가" 다.
//   폼 검증이 이 값을 봐야 한다 — 아래 missingRequiredFields 주석 참고.
export function useCallRegisterCore(adminMode, companyCode = '') {
  // ── refs ──────────────────────────────────────────────────────────────────
  /** 공휴일 조회 결과 캐시: { '2026': Promise<Set<string>> } */
  const holidayRef = useRef({});
  /** 컬럼 리사이즈 드래그 상태 저장 */
  const resizeRef = useRef(null);
  /** 등록 폼 내용 textarea: form.content 변경 시 높이 자동 확장 */
  const formContentRef = useRef(null);
  /** 수정 모달 내용 textarea: 모달 열릴 때 기존 내용 길이에 맞게 높이 조정 */
  const editContentRef = useRef(null);

  // ── 기본 상태 ─────────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [isExternalWorker, setIsExternalWorker] = useState(false);
  // 상품칸에 글자로 박히는 분류 이름. 상품 관리 > "회사에 붙이기" 에서 '카테고리 고정' 일 때만 채워진다.
  const [externalCategoryName, setExternalCategoryName] = useState('');
  // 카테고리 고정 모드 여부: true=분류 이름 고정 / false=상품 드롭다운
  const [isExtCategoryFixed, setIsExtCategoryFixed] = useState(true);
  // 상품 관리 > "회사에 붙이기" 설정이 적용됐는지. 적용됐으면 표시 방식을 그 설정이 정하고,
  //   아니면 예전대로 외부직원 여부가 정한다(설정 전에는 화면이 하나도 안 바뀐다).
  const [salesPersonNames, setSalesPersonNames] = useState([]);
  const [list, setList] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [fixedSalesPerson, setFixedSalesPerson] = useState(false);
  const [naverText, setNaverText] = useState('');
  // 등록 직후 10초간 표시되는 성공 텍스트
  const [submitResultText, setSubmitResultText] = useState('');

  // ── 수정 모달 상태 ────────────────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(DEFAULT_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [keepingId, setKeepingId] = useState(null); // 킵 토글 중인 콜 ID

  // ── 킵 해제 모달 상태 ─────────────────────────────────────────────────────
  const [keepReleaseOpen, setKeepReleaseOpen] = useState(false);
  const [keepReleaseTarget, setKeepReleaseTarget] = useState(null);
  const [keepReleaseDate, setKeepReleaseDate] = useState('');
  const [keepReleaseSales, setKeepReleaseSales] = useState('');
  const [keepReleaseMemo, setKeepReleaseMemo] = useState('');
  const [keepReleaseSaving, setKeepReleaseSaving] = useState(false);
  const [keepReleaseActions, setKeepReleaseActions] = useState(null); // 창 버튼 가능 여부 { memo, close, reassign } — null = 모름(전부 켬)

  // ── 필터 상태 ─────────────────────────────────────────────────────────────
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [filterKeep, setFilterKeep] = useState(false); // 킵목록만 보기
  const [filterQuery, setFilterQuery] = useState('');   // 전체 필드 텍스트 검색
  const [listPage, setListPage] = useState(1);          // 목록 페이지(1부터)

  // ── 컬럼 너비 상태 (form/list 공유) ────────────────────────────────────────
  const [sharedColWidths, setSharedColWidths] = useState(
    () => FORM_COLUMNS.map((c) => Math.max(MIN_COL_WEIGHT, c.w))
  );

  // ── 컬럼 표시 목록 (등록 폼은 2줄 격자라 목록만 열 너비를 쓴다) ────────────
  const visibleListColumns = useMemo(
    () => (adminMode ? LIST_COLUMNS : LIST_COLUMNS.filter((c) => c.key !== 'user_name')),
    [adminMode]
  );

  // ── 그리드 템플릿 문자열 (컬럼 너비 드래그 반영) ──────────────────────────
  const listTemplate = useMemo(() => (
    makeFluidTemplate(
      visibleListColumns.map((col) => {
        const baseIndex = LIST_COLUMNS.findIndex((x) => x.key === col.key);
        const nextW = baseIndex < 0
          ? Math.max(MIN_COL_WEIGHT, col.w)
          : Math.max(MIN_COL_WEIGHT, sharedColWidths[baseIndex] ?? col.w);
        return { ...col, w: nextW };
      })
    )
  ), [sharedColWidths, visibleListColumns]);

  // 가로 스크롤 감지용: 목록 그리드의 최소 px 합산
  const listMinWidth = useMemo(
    () => visibleListColumns.reduce((sum, col) => sum + Math.max(MIN_COL_PX, col.min_px || MIN_COL_PX), 0),
    [visibleListColumns]
  );

  // ── 폼 유효성 파생 상태 ───────────────────────────────────────────────────
  const requiredFields = useMemo(() => REQUIRED_FIELDS, []);
  const missingRequiredFields = useMemo(
    () => requiredFields.filter((key) => {
      // ticket_type_id: 카테고리 고정 모드에서는 상품 칸이 읽기전용이고 서버가 값을 정한다
      //   → 클라이언트가 상품을 요구하면 아무것도 고를 수 없는 잠금 상태가 된다.
      //
      // 판정 기준은 **배정**이다. 신분(worker_type)이 아니다.
      //   메뉴·라우트가 회사별로 갈리므로(TM_FEATURE_EXT_LNJ 배정 → /my/tm-calls-lnj),
      //   companyCode 가 있다는 것 자체가 "그 회사 콜을 등록하도록 배정받았다" 는 뜻이다.
      //   예전에는 isExternalWorker(worker_type==='external') 만 봐서,
      //   LNJ 배정을 받은 파인그룹 직원이 '입력 필요: 상품' 으로 등록을 못 했다.
      //   전송값(useCallRegisterSubmit)·칸 표시(RegistrationForm)·자동설정은 이미 companyCode 를 따랐고
      //   검증만 빠져 있었다.
      // 파인그룹 내부 등록은 companyCode 가 없어 종전대로 상품 선택이 필수다.
      if (key === 'ticket_type_id' && externalCategoryName && (isExternalWorker || Boolean(companyCode))) return false;
      return String(form[key] ?? '').trim().length === 0;
    }),
    [form, requiredFields, externalCategoryName, isExternalWorker, companyCode]
  );
  const isSubmitReady = missingRequiredFields.length === 0;

  // ── 목록 파생 상태 ────────────────────────────────────────────────────────
  const filteredList = useMemo(() => {
    // 기본 정렬: 콜일자 최신순 → 이름순
    let sorted = [...list].sort(compareByCallDateDescThenNameAsc);
    // 킵만 보기 필터: 관리자·직원 모두 적용
    if (filterKeep) sorted = sorted.filter((row) => Boolean(row?.is_keep));
    // 전체 필드 텍스트 검색 — 주소 포함. 2026-10-23 직원 화면에도 같은 검색칸("주소 검색도 추가해줘", 사용자 선택 a)
    const q = filterQuery.trim().toLowerCase();
    if (q) {
      sorted = sorted.filter((row) => {
        const fields = [
          row.company_name, row.phone, row.mobile_phone, row.ceo_name,
          row.address, row.address_detail, row.region, row.industry,
          row.content, row.feedback, row.sales_person_name, row.user_name,
          row.keep_memo,
          row.ticket_type,
          // contract_items 배열 → 상품명 문자열로 검색 가능하도록 변환
          ...(Array.isArray(row.contract_items)
            ? row.contract_items.map((i) => i.ticketTypeName || '')
            : [row.contract_ticket_type]),
        ];
        return fields.some((v) => String(v || '').toLowerCase().includes(q));
      });
    }
    return sorted;
  }, [filterKeep, filterQuery, list]);

  // ── 목록 페이지 나누기 (2026-10-23, 계약관리와 같은 방식 — 받은 목록을 화면에서 자른다) ──
  //   검색·킵을 바꾸면 1페이지로. 목록이 줄어 지금 페이지가 사라지면 마지막 페이지로 당긴다.
  const listTotalPages = Math.max(1, Math.ceil(filteredList.length / CALL_LIST_PAGE_SIZE));
  useEffect(() => { setListPage(1); }, [filterKeep, filterQuery]);
  useEffect(() => { setListPage((p) => Math.min(p, listTotalPages)); }, [listTotalPages]);
  const pagedList = useMemo(
    () => filteredList.slice((listPage - 1) * CALL_LIST_PAGE_SIZE, listPage * CALL_LIST_PAGE_SIZE),
    [filteredList, listPage],
  );

  // 외부직원 ticket_type 고정값 (빈 문자열이면 아직 로드 전)
  const currentExternalCategoryName = externalCategoryName;

  // ── 직원 페이지 헤더용 날짜/건수 ────────────────────────────────────────────
  // todayKST, nowKST: utils/date.js 사용 (new Date + KST 수동 변환 금지)
  /** KST 기준 오늘 날짜 문자열 (YYYY-MM-DD) */
  const todayKstStr = useMemo(
    () => todayKST(),
    []
  );
  /** 오늘 등록한 콜 건수 */
  const todayCount = useMemo(
    () => list.filter((r) => toDateString(r.call_date || '') === todayKstStr).length,
    [list, todayKstStr]
  );
  /** 헤더에 표시할 한국어 날짜 레이블 (예: 2026년 3월 12일 (목)) */
  const todayLabel = useMemo(
    () => nowKST().toLocaleDateString('ko-KR', {
      year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
    }),
    []
  );

  return {
    // refs
    holidayRef, resizeRef, formContentRef, editContentRef,
    // 기본 상태
    isMobile, setIsMobile,
    loading, setLoading,
    saving, setSaving,
    ticketTypes, setTicketTypes,
    isExternalWorker, setIsExternalWorker,
    externalCategoryName, setExternalCategoryName,
    isExtCategoryFixed, setIsExtCategoryFixed,
    salesPersonNames, setSalesPersonNames,
    list, setList,
    form, setForm,
    fixedSalesPerson, setFixedSalesPerson,
    naverText, setNaverText,
    submitResultText, setSubmitResultText,
    // 수정 모달
    editOpen, setEditOpen,
    editTarget, setEditTarget,
    editForm, setEditForm,
    editSaving, setEditSaving,
    deletingId, setDeletingId,
    keepingId, setKeepingId,
    // 킵 해제 모달
    keepReleaseOpen, setKeepReleaseOpen,
    keepReleaseTarget, setKeepReleaseTarget,
    keepReleaseDate, setKeepReleaseDate,
    keepReleaseSales, setKeepReleaseSales,
    keepReleaseMemo, setKeepReleaseMemo,
    keepReleaseSaving, setKeepReleaseSaving,
    keepReleaseActions, setKeepReleaseActions,
    // 필터
    ownerOptions, setOwnerOptions,
    filterKeep, setFilterKeep,
    filterQuery, setFilterQuery,
    listPage, setListPage, listTotalPages, pagedList,
    // 컬럼 너비
    sharedColWidths, setSharedColWidths,
    // 파생 상태 (useMemo)
    visibleListColumns, listTemplate, listMinWidth,
    requiredFields, missingRequiredFields, isSubmitReady,
    filteredList,
    currentExternalCategoryName,
    todayKstStr, todayCount, todayLabel,
  };
}
